/**
 * Sotto-viste di modifica dati per il dialogo "Dati e relazioni" (sezioni di tipo `edit`).
 *
 * - info: patch del tipo collegato (Company/FoodService/Service/Retailer), inline.
 * - sede:
 *     - Company: la sede principale è il billing Branch → si modifica il Branch (o se ne crea uno).
 *     - FoodService/Service/Retailer: si modificano i campi indirizzo dell'entità stessa.
 * - companyYear: relazione a selezione singola. Mostra tutti i CompanyYear dell'azienda con un
 *   radio (più "crea nuovo"); al salvataggio collega via Relationships360 l'anno scelto (e scollega
 *   gli altri). Di default nessun anno è collegato → fatturato/dipendenti "—".
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Form, Button, Table } from 'reactstrap';
import { EntityEditFetcher } from './EntityEditFetcher';
import {
	useRegisterEditSave,
	useRegisterSubForm,
} from './relations360SaveRegistry';
import { ValidationErrorsModal } from './ValidationErrorsModal';
import { Spinner } from './MainContentSpinner';
import { renderField } from '../utils/form';
import { graphqlQuery } from '../utils/graphql';
import { client } from '../utils/client';
import { t } from '../utils/labels';
import { ENTITY } from '../utils/entities';
import {
	ConnectedEntityConfig,
	EditSectionKey,
	editSectionFields,
	buildCompanyYearsQuery,
	fair360CreateTransformer,
} from '../utils/entities/companyData360';
import {
	applyRelationships360Bulk,
	bidirectionalCreatePairs,
	RELATIONS_LINKS_QUERY,
	RelationLinks,
	emptyLinks,
	parseLinks,
} from '../utils/entities/relationships360';

import { CompanyYearCreateMutation } from '../graphql/mutations/CompanyYearCreateMutation';
import { CompanyYearPatchMutation } from '../graphql/mutations/CompanyYearPatchMutation';
import { CompanyYearEditQuery } from '../graphql/query/CompanyYearEditQuery';
import { deleteNodeMutation } from '../graphql/mutations/DeleteNodeMutation';
import CompanyYearIntro from '../static/introspection/CompanyYear.json';
import CompanyYearCreateIntro from '../static/introspection/CompanyYearCreateInput.json';
import CompanyYearPatchIntro from '../static/introspection/CompanyYearPatchInput.json';

import { BranchPatchMutation } from '../graphql/mutations/BranchPatchMutation';
import { BranchCreateMutation } from '../graphql/mutations/BranchCreateMutation';
import { BranchEditQuery } from '../graphql/query/BranchEditQuery';
import { CompanyPatchMutation } from '../graphql/mutations/CompanyPatchMutation';
import { CompanyEditQuery } from '../graphql/query/CompanyEditQuery';
import BranchIntro from '../static/introspection/Branch.json';
import BranchPatchIntro from '../static/introspection/BranchPatchInput.json';
import BranchCreateIntro from '../static/introspection/BranchCreateInput.json';
import CompanyIntro from '../static/introspection/Company.json';
import CompanyPatchIntro from '../static/introspection/CompanyPatchInput.json';

import { fairParticipationCreateMutation } from '../graphql/mutations/FairParticipationCreateMutation';
import FairParticipationIntro from '../static/introspection/FairParticipation.json';
import FairParticipationCreateIntro from '../static/introspection/FairParticipationCreateInput.json';
import { GoodClaimCreateMutation } from '../graphql/mutations/GoodClaimCreateMutation';
import GoodClaimIntro from '../static/introspection/GoodClaim.json';
import GoodClaimCreateIntro from '../static/introspection/GoodClaimCreateInput.json';

/**
 * Form inline (riusa renderField come EntityEditModalView).
 *
 * Non ha un proprio bottone di salvataggio: registra il proprio stato "sporco" e il proprio submit
 * nel dialogo 360, che ha un unico Salva globale nel footer.
 *
 * `subForm` se il form è una sotto-vista su una singola entità: il footer del dialogo espone
 * allora le azioni di questo form al posto del Salva globale.
 */
export const InlineEditForm: React.FC<any> = ({
	fields,
	mutatorFactory,
	onSubmit,
	validationModalOpen,
	toggleValidationModal,
	isChanged,
	subForm,
}) => {
	useRegisterEditSave({ isDirty: !!isChanged, save: onSubmit });
	useRegisterSubForm(
		subForm
			? {
					isDirty: !!isChanged,
					kind: subForm.kind,
					submit: onSubmit,
					cancel: subForm.cancel,
			  }
			: null,
	);

	return (
		<>
			<Form className="Form">
				{fields.map((f: any) => renderField(f, f.name, mutatorFactory(f), true))}
			</Form>
			<ValidationErrorsModal
				open={validationModalOpen}
				onToggle={toggleValidationModal}
			/>
		</>
	);
};

/**
 * Sotto-vista "crea collegamento" di una sezione relazione (FairParticipation / Certificazioni).
 * - fairParticipation: crea una FairParticipation (Company fissa, ricerca su tutte le fiere).
 * - companyCertification: associa all'azienda CompanyCertification esistenti (patch su companyCertifications).
 * Al salvataggio il chiamante ricarica i suggerimenti.
 */
export const Relation360CreateView: React.FC<{
	createKind: 'fairParticipation' | 'companyCertification' | 'goodClaim';
	connectedCompany: any;
	connectedId: string;
	onSaved: () => void;
	onCancel: () => void;
}> = ({ createKind, connectedCompany, connectedId, onSaved, onCancel }) => {
	const subForm = { kind: 'create' as const, cancel: onCancel };
	if (createKind === 'fairParticipation') {
		return (
			<EntityEditFetcher
				create={true}
				modal={true}
				fetcher={null}
				mutation={fairParticipationCreateMutation}
				entityName={ENTITY.FAIR_PARTICIPATION}
				typeIntro={FairParticipationIntro}
				inputTypeIntro={FairParticipationCreateIntro}
				fields={['Fair', 'Company', 'pavilion', 'stand', 'note']}
				transformer={fair360CreateTransformer(connectedCompany)}
				onSubmit={() => onSaved()}
			>
				{(args: any) => <InlineEditForm {...args} subForm={subForm} />}
			</EntityEditFetcher>
		);
	}
	if (createKind === 'goodClaim') {
		// crea una nuova GoodClaim (globale), poi il chiamante ricarica i suggerimenti/link
		return (
			<EntityEditFetcher
				create={true}
				modal={true}
				fetcher={null}
				mutation={GoodClaimCreateMutation}
				entityName={ENTITY.GOOD_CLAIM}
				typeIntro={GoodClaimIntro}
				inputTypeIntro={GoodClaimCreateIntro}
				fields={['name']}
				onSubmit={() => onSaved()}
			>
				{(args: any) => <InlineEditForm {...args} subForm={subForm} />}
			</EntityEditFetcher>
		);
	}
	// companyCertification: associa all'azienda CompanyCertification esistenti
	return (
		<EntityEditFetcher
			modal={true}
			fetcher={graphqlQuery(CompanyEditQuery, { id: connectedId })}
			id={connectedId}
			mutation={CompanyPatchMutation}
			entityName={ENTITY.COMPANY}
			typeIntro={CompanyIntro}
			inputTypeIntro={CompanyPatchIntro}
			fields={['companyCertifications']}
			onSubmit={() => onSaved()}
		>
			{(args: any) => <InlineEditForm {...args} subForm={subForm} />}
		</EntityEditFetcher>
	);
};

interface EditViewProps {
	config: ConnectedEntityConfig;
	sectionKey: EditSectionKey;
	connectedId: string;
	company360Id: string;
	summary: any;
	linkedCompanyYear?: any;
	onSaved: () => void;
}

/** Info + sede (FoodService/Service/Retailer): patch del tipo collegato. */
const GenericEditSection: React.FC<EditViewProps> = ({
	config,
	sectionKey,
	connectedId,
	onSaved,
}) => (
	<EntityEditFetcher
		modal={true}
		fetcher={graphqlQuery(config.editQuery, { id: connectedId })}
		id={connectedId}
		mutation={config.patchMutation}
		entityName={config.entityName}
		typeIntro={config.typeIntro}
		inputTypeIntro={config.patchInputIntro}
		fields={editSectionFields(config, sectionKey)}
		onSubmit={() => onSaved()}
	>
		{(args: any) => <InlineEditForm {...args} />}
	</EntityEditFetcher>
);

const BRANCH_SEDE_PATCH_FIELDS = [
	'name',
	'address1',
	'address2',
	'postalCode',
	'city',
	'StateProvince',
	'Country',
];
const BRANCH_SEDE_CREATE_FIELDS = ['name', 'type', ...BRANCH_SEDE_PATCH_FIELDS.slice(1)];

/** Sede principale di una Company = il suo billing Branch: lo si modifica (o lo si crea). */
const BranchSedeEditSection: React.FC<EditViewProps> = ({
	connectedId,
	summary,
	onSaved,
}) => {
	const branchId = summary && summary.billingBranch && summary.billingBranch.id;

	if (branchId) {
		return (
			<EntityEditFetcher
				modal={true}
				fetcher={graphqlQuery(BranchEditQuery, { id: branchId })}
				id={branchId}
				mutation={BranchPatchMutation}
				entityName={ENTITY.BRANCH}
				typeIntro={BranchIntro}
				inputTypeIntro={BranchPatchIntro}
				fields={BRANCH_SEDE_PATCH_FIELDS}
				onSubmit={() => onSaved()}
			>
				{(args: any) => <InlineEditForm {...args} />}
			</EntityEditFetcher>
		);
	}

	return (
		<EntityEditFetcher
			modal={true}
			create={true}
			fetcher={null}
			mutation={BranchCreateMutation}
			entityName={ENTITY.BRANCH}
			typeIntro={BranchIntro}
			inputTypeIntro={BranchCreateIntro}
			fields={BRANCH_SEDE_CREATE_FIELDS}
			beforeSaveTransformer={(values: any) => ({
				...values,
				Company: { id: connectedId },
			})}
			onSubmit={async (branch: any) => {
				try {
					await client.mutate({
						mutation: CompanyPatchMutation,
						variables: {
							id: connectedId,
							changes: { billingBranch: { id: branch.id } },
						},
					});
				} catch (e) {
					// se la patch del billingBranch fallisce, il branch è comunque creato
				}
				onSaved();
			}}
		>
			{(args: any) => <InlineEditForm {...args} />}
		</EntityEditFetcher>
	);
};

/** Global id dei record Relationships360 che collegano la sorgente a CompanyYear (entrambe le direzioni). */
const collectCompanyYearLinkIds = (links: RelationLinks): string[] => {
	const ids: string[] = [];
	links.from.forEach((n) => {
		if (n.toRecordType === 'CompanyYear') ids.push(n.id);
	});
	links.to.forEach((n) => {
		if (n.fromRecordType === 'CompanyYear') ids.push(n.id);
	});
	return ids;
};

const COMPANY_YEAR_FIELDS = [
	'year',
	'revenue',
	'numEmployees',
	'exportRevenue',
	'ebitda',
	'gain',
	'mol',
];

type CompanyYearMode =
	| { kind: 'list' }
	| { kind: 'create' }
	| { kind: 'edit'; id: string };

/**
 * CompanyYear: relazione a selezione singola. Radio su tutti gli anni dell'azienda, più creazione,
 * modifica ed eliminazione dei singoli anni (come le evidenze di Good360).
 */
const CompanyYearSection: React.FC<EditViewProps> = ({
	config,
	connectedId,
	company360Id,
	linkedCompanyYear,
	onSaved,
}) => {
	const [years, setYears] = useState<any[]>([]);
	const [links, setLinks] = useState<RelationLinks>(emptyLinks());
	const [loading, setLoading] = useState(true);
	const [mode, setMode] = useState<CompanyYearMode>({ kind: 'list' });
	const [saving, setSaving] = useState(false);
	const [busy, setBusy] = useState(false);
	const linkedYearId = linkedCompanyYear ? linkedCompanyYear.id : '';
	const [selectedId, setSelectedId] = useState<string>(linkedYearId);

	const backToList = () => setMode({ kind: 'list' });

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [yRes, lRes] = await Promise.all([
				client.query<any, any>({
					query: buildCompanyYearsQuery(config),
					variables: { id: connectedId },
					fetchPolicy: 'no-cache',
				}),
				client
					.query<any, any>({
						query: RELATIONS_LINKS_QUERY,
						variables: { id: company360Id },
						fetchPolicy: 'no-cache',
					})
					.catch(() => null),
			]);
			const list = ((yRes.data && yRes.data.entity && yRes.data.entity.companyYears) || [])
				.slice()
				.sort((a: any, b: any) => b.year - a.year);
			setYears(list);
			setLinks(lRes ? parseLinks(lRes.data) : emptyLinks());
		} finally {
			setLoading(false);
		}
	}, [config, connectedId, company360Id]);

	useEffect(() => {
		load();
	}, [load]);

	const save = useCallback(async () => {
		setSaving(true);
		try {
			const deleteIds = collectCompanyYearLinkIds(links);
			const creates = selectedId
				? bidirectionalCreatePairs(company360Id, selectedId)
				: [];
			await applyRelationships360Bulk(deleteIds, creates);
		} catch (e) {
			// backend non ancora pronto per i link CompanyYear: ignoro
		} finally {
			setSaving(false);
			onSaved();
		}
	}, [links, selectedId, company360Id, onSaved]);

	/** Elimina un anno; se era quello collegato, la selezione torna a "nessun anno". */
	const remove = async (id: string) => {
		if (!window.confirm(t`CompanyData360/year/deleteConfirm`)) return;
		setBusy(true);
		try {
			await client.mutate({ mutation: deleteNodeMutation, variables: { id } });
			if (selectedId === id) setSelectedId('');
			await load();
			onSaved();
		} finally {
			setBusy(false);
		}
	};

	// nelle sotto-viste lo "sporco" è quello del form, non quello della selezione
	useRegisterEditSave({
		isDirty: mode.kind === 'list' && !loading && selectedId !== linkedYearId,
		save,
	});

	if (loading) return <Spinner show={true} />;

	if (mode.kind === 'create') {
		return (
			<EntityEditFetcher
				create={true}
				modal={true}
				fetcher={null}
				mutation={CompanyYearCreateMutation}
				entityName={ENTITY.COMPANY_YEAR}
				typeIntro={CompanyYearIntro}
				inputTypeIntro={CompanyYearCreateIntro}
				fields={COMPANY_YEAR_FIELDS}
				beforeSaveTransformer={(values: any) => ({
					...values,
					[config.companyYearFkField]: { id: connectedId },
				})}
				onSubmit={async (created: any) => {
					backToList();
					await load();
					if (created && created.id) setSelectedId(created.id);
				}}
			>
				{(args: any) => (
					<InlineEditForm
						{...args}
						subForm={{ kind: 'create', cancel: backToList }}
					/>
				)}
			</EntityEditFetcher>
		);
	}

	if (mode.kind === 'edit') {
		return (
			<EntityEditFetcher
				modal={true}
				fetcher={graphqlQuery(CompanyYearEditQuery, { id: mode.id })}
				id={mode.id}
				mutation={CompanyYearPatchMutation}
				entityName={ENTITY.COMPANY_YEAR}
				typeIntro={CompanyYearIntro}
				inputTypeIntro={CompanyYearPatchIntro}
				fields={COMPANY_YEAR_FIELDS}
				onSubmit={async () => {
					backToList();
					await load();
					onSaved();
				}}
			>
				{(args: any) => (
					<InlineEditForm {...args} subForm={{ kind: 'edit', cancel: backToList }} />
				)}
			</EntityEditFetcher>
		);
	}

	const fmt = (n: any) => (n == null ? '' : Number(n).toLocaleString('it-IT'));

	return (
		<div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					marginBottom: 8,
				}}
			>
				<Button
					color="success"
					disabled={saving || busy}
					onClick={() => setMode({ kind: 'create' })}
				>
					<i className="fa fa-plus" /> {t`create new`}
				</Button>
			</div>

			<Table hover>
				<thead>
					<tr>
						<th style={{ width: 40 }} />
						<th>{t`CompanyYear/year/label`}</th>
						<th>{t`CompanyYear/revenue/label`}</th>
						<th>{t`CompanyYear/numEmployees/label`}</th>
						<th style={{ width: 160 }} />
					</tr>
				</thead>
				<tbody>
					<tr style={{ cursor: 'pointer' }} onClick={() => setSelectedId('')}>
						<td>
							<input
								type="radio"
								checked={!selectedId}
								onChange={() => setSelectedId('')}
							/>
						</td>
						<td colSpan={4} className="text-muted">
							{t`CompanyData360/noYear`}
						</td>
					</tr>
					{years.map((y) => (
						<tr
							key={y.id}
							style={{ cursor: 'pointer' }}
							onClick={() => setSelectedId(y.id)}
						>
							<td>
								<input
									type="radio"
									checked={selectedId === y.id}
									onChange={() => setSelectedId(y.id)}
								/>
							</td>
							<td>{y.year}</td>
							<td>{fmt(y.revenue)}</td>
							<td>{fmt(y.numEmployees)}</td>
							{/* le azioni non devono cambiare l'anno collegato: fermo il click della riga */}
							<td
								className="text-right"
								onClick={(e) => e.stopPropagation()}
							>
								<Button
									size="sm"
									color="success"
									className="margin-right"
									disabled={busy}
									onClick={() => setMode({ kind: 'edit', id: y.id })}
								>
									<i className="fa fa-pencil" />
								</Button>
								<Button
									size="sm"
									color="danger"
									disabled={busy}
									onClick={() => remove(y.id)}
								>
									<i className="fa fa-trash" />
								</Button>
							</td>
						</tr>
					))}
				</tbody>
			</Table>
			{years.length === 0 && (
				<p className="text-muted">{t`CompanyData360/noYears`}</p>
			)}
		</div>
	);
};

export const CompanyData360EditView: React.FC<EditViewProps> = (props) => {
	if (props.sectionKey === 'companyYear') return <CompanyYearSection {...props} />;
	if (props.sectionKey === 'sede' && props.config.entityName === ENTITY.COMPANY)
		return <BranchSedeEditSection {...props} />;
	return <GenericEditSection {...props} />;
};
